import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Paper,
  Tabs,
  Tab,
  TextField,
  Divider,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import LinkIcon from '@mui/icons-material/Link';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const AudioUploader = ({ onProcessed }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [soundcloudUrl, setSoundcloudUrl] = useState('');
  const [urlError, setUrlError] = useState('');

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    resetUpload();
    setUrlError('');
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];

    if (!file) return;

    // Check file type
    const fileType = file.type;
    if (!fileType.includes('audio')) {
      setUploadError('Please upload an audio file (mp3, m4a, wav, ogg, flac)');
      return;
    }

    setUploadedFile(file);
    setUploadError('');
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'audio/*': ['.mp3', '.m4a', '.aac', '.wav', '.ogg', '.flac'],
    },
    maxFiles: 1,
  });

  const handleProcess = async () => {
    if (!uploadedFile) return;

    setIsUploading(true);
    setUploadError('');

    const formData = new FormData();
    formData.append('file', uploadedFile);

    try {
      const response = await axios.post(`${API_URL}/process/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data && response.data.filename) {
        onProcessed(response.data.filename);
      } else {
        setUploadError('Something went wrong with the processing');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error processing audio:', error);
      setUploadError(
        error.response?.data?.detail || 'Error processing the audio file. Please try again.'
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleSoundcloudProcess = async () => {
    if (!soundcloudUrl) {
      setUrlError('Please enter a SoundCloud URL');
      return;
    }

    // Simple validation for SoundCloud URL format
    if (!soundcloudUrl.includes('soundcloud.com') && !soundcloudUrl.includes('snd.sc')) {
      setUrlError('Please enter a valid SoundCloud URL');
      return;
    }

    setIsUploading(true);
    setUrlError('');

    try {
      const response = await axios.post(`${API_URL}/process-soundcloud/`, {
        url: soundcloudUrl,
      });

      if (response.data && response.data.filename) {
        onProcessed(response.data.filename);
      } else {
        setUrlError('Something went wrong with the processing');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error processing SoundCloud URL:', error);
      setUrlError(
        error.response?.data?.detail || 'Error processing the SoundCloud URL. Please try again.'
      );
    } finally {
      setIsUploading(false);
    }
  };

  const resetUpload = () => {
    setUploadedFile(null);
    setUploadError('');
    setSoundcloudUrl('');
  };

  const renderFileUpload = () => (
    <>
      {!uploadedFile ? (
        <Box
          {...getRootProps()}
          sx={{
            border: '2px dashed #666',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            bgcolor: isDragActive ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: '#fff',
              bgcolor: 'rgba(255, 255, 255, 0.05)',
            },
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {isDragActive ? 'Drop the audio here' : 'Drag audio file here'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            or click to select a file
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2 }}>
            Supported formats: MP3, M4A, WAV, OGG, FLAC
          </Typography>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center' }}>
          <MusicNoteIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            File Ready for Processing
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            {uploadedFile.name} ({(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB)
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleProcess}
              disabled={isUploading}
              startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isUploading ? 'Processing...' : 'Process Audio'}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={resetUpload}
              disabled={isUploading}
            >
              Change File
            </Button>
          </Box>
        </Box>
      )}

      {uploadError && (
        <Typography color="error" variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
          {uploadError}
        </Typography>
      )}
    </>
  );

  const renderSoundcloudInput = () => (
    <Box sx={{ p: 2 }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <LinkIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Enter SoundCloud URL
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Paste a link to any SoundCloud track
        </Typography>
      </Box>

      <TextField
        fullWidth
        variant="outlined"
        label="SoundCloud URL"
        placeholder="https://soundcloud.com/artist/track"
        value={soundcloudUrl}
        onChange={(e) => setSoundcloudUrl(e.target.value)}
        disabled={isUploading}
        error={!!urlError}
        helperText={urlError}
        sx={{ mb: 3 }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSoundcloudProcess}
          disabled={isUploading || !soundcloudUrl}
          startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{ minWidth: '150px' }}
        >
          {isUploading ? 'Processing...' : 'Process Track'}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        bgcolor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: 4,
        width: '100%',
        maxWidth: 500,
      }}
    >
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        centered
        sx={{
          mb: 2,
          '& .MuiTabs-indicator': {
            backgroundColor: 'primary.main',
          },
        }}
      >
        <Tab label="Upload File" />
        <Tab label="SoundCloud URL" />
      </Tabs>

      <Divider sx={{ mb: 3 }} />

      {activeTab === 0 ? renderFileUpload() : renderSoundcloudInput()}
    </Paper>
  );
};

export default AudioUploader;
